import { fetchAuth } from "../../../shared/api/ApiService";
import {
    IEventActorRef,
    ILessonEvent,
    ILessonEventReview,
    IReviewLessonEventResponse,
    IListLessonEventsRequest,
    IListLessonEventsResponse,
    IReviewLessonEventRequest,
} from "../model/types";

type TAnyRecord = Record<string, any>;

const mapOptionalId = (...values: unknown[]): string => {
    const value = values.find(item => item !== undefined && item !== null)
    const id = String(value ?? '')

    return id === '0' ? '' : id
}

const mapActor = (actor?: TAnyRecord): IEventActorRef | undefined => {
    if(!actor) return undefined;

    return {
        userId: String(actor.userId ?? actor.user_id ?? ''),
        fullName: actor.fullName ?? actor.full_name ?? '',
        role: actor.role ?? '',
        roleName: actor.roleName ?? actor.role_name ?? '',
    }
}

const mapReview = (review: TAnyRecord): ILessonEventReview => ({
    id: String(review.id ?? ''),
    eventId: String(review.eventId ?? review.event_id ?? ''),
    reviewLevel: review.reviewLevel ?? review.review_level ?? 'LESSON_EVENT_REVIEW_LEVEL_UNSPECIFIED',
    status: review.status ?? 'LESSON_EVENT_REVIEW_STATUS_UNSPECIFIED',
    reviewedBy: mapActor(review.reviewedBy ?? review.reviewed_by),
    comment: review.comment ?? '',
    payload: review.payload ?? {},
    createdAt: review.createdAt ?? review.created_at ?? '',
})

const mapEvent = (event: TAnyRecord): ILessonEvent => ({
    id: String(event.id ?? ''),
    lessonId: String(event.lessonId ?? event.lesson_id ?? ''),
    trialLessonId: mapOptionalId(event.trialLessonId, event.trial_lesson_id),
    student: event.student ?? {},
    tutor: event.tutor ?? {},
    tgAccount: event.tgAccount ?? event.tg_account ?? {},
    tag: event.tag,
    eventType: event.eventType ?? event.event_type ?? 'LESSON_EVENT_TYPE_UNSPECIFIED',
    faultParty: event.faultParty ?? event.fault_party ?? 'LESSON_EVENT_FAULT_PARTY_UNSPECIFIED',
    createdBy: mapActor(event.createdBy ?? event.created_by),
    payload: event.payload ?? {},
    createdAt: event.createdAt ?? event.created_at ?? '',
    reviews: (event.reviews ?? []).map(mapReview),
    availableActions: event.availableActions ?? event.available_actions ?? [],
})

class EventsService {
    async listLessonEvents(data: IListLessonEventsRequest): Promise<IListLessonEventsResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/events/lesson-events/list`, {
            method: 'POST',
            body: JSON.stringify(data),
        })
        const response = await res.json()

        return {
            viewer: response.viewer,
            events: (response.events ?? []).map(mapEvent),
            total: String(response.total ?? 0),
        }
    }

    async reviewLessonEvent(data: IReviewLessonEventRequest): Promise<IReviewLessonEventResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/events/lesson-events/${data.eventId}/reviews`, {
            method: 'POST',
            body: JSON.stringify(data),
        })
        const response = await res.json()

        return {
            viewer: response.viewer,
            event: mapEvent(response.event ?? {}),
        }
    }

    async getLessonEvent(eventId: string): Promise<IReviewLessonEventResponse> {
        const res = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/events/lesson-events/${eventId}`)
        const response = await res.json()

        return {
            viewer: response.viewer,
            event: mapEvent(response.event ?? {}),
        }
    }
}

export const eventsService = new EventsService()
